/*
 * /advertisers
 * ------------
 * The page for sponsors, hotels, shops and festival organisers.
 *
 * The ad mockups use made-up placeholder businesses on purpose. Do not put
 * real brand names here without that business's written permission.
 */
import {
  ArrowRight,
  Calendar,
  Check,
  Hotel,
  Mail,
  Megaphone,
  PartyPopper,
  ShoppingBasket,
  Store,
  Utensils,
  X as XIcon,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const CONTACT_EMAIL = "partnerships@h-plate.com";

/** Builds a mailto link to the partnerships inbox with a subject line. */
function mailto(subject: string) {
  return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`;
}

/* ─── Mini phone mockup with a configurable ad placement ──────────────── */

interface AdMockupProps {
  brand: string;
  brandTagline: string;
  brandColor: string;
  placement:
    | "launch_splash"
    | "home_banner"
    | "sheet_offers"
    | "deal_code"
    | "ride_complete"
    | "festival_pin"
    | "shop_local";
  icon?: React.ElementType;
}

/**
 * A small drawn phone showing where an ad sits in the H-Plate app.
 */
function AdMockup({
  brand,
  brandTagline,
  brandColor,
  placement,
  icon: Icon = Store,
}: AdMockupProps) {
  return (
    <div className="w-[200px] mx-auto">
      <div className="rounded-[1.7rem] bg-[#0d1117] p-[7px] shadow-[0_30px_60px_-28px_rgba(2,20,14,0.6)]">
        <div className="relative bg-black rounded-[1.35rem] overflow-hidden aspect-[9/19]">
          <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-14 h-3.5 bg-black rounded-full z-10" />

          <div className="absolute inset-0 flex flex-col bg-[#e9efe9]">
            <div className="px-3 pt-2 pb-1 flex items-center justify-between text-slate-800 text-[7px] font-semibold">
              <span>9:41</span>
              <span>5G</span>
            </div>

            {/* Map */}
            <div className="flex-1 relative overflow-hidden bg-[#e4ece4]">
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 360" preserveAspectRatio="xMidYMid slice" aria-hidden>
                <path d="M-10 90 C 60 70, 90 140, 210 110" stroke="#ffffff" strokeWidth="9" fill="none" />
                <path d="M40 -10 C 60 120, 30 220, 80 380" stroke="#ffffff" strokeWidth="7" fill="none" />
                <path d="M-10 250 C 70 230, 130 280, 210 240" stroke="#ffffff" strokeWidth="6" fill="none" />
                <path d="M150 -10 C 140 100, 170 200, 150 380" stroke="#cfe0d2" strokeWidth="14" fill="none" />
                <path d="M40 -10 C 60 120, 30 220, 80 380" stroke="#10b981" strokeWidth="2.5" fill="none" strokeDasharray="0" />
              </svg>

              {placement === "launch_splash" && (
                <div
                  className="absolute inset-0 flex flex-col justify-between p-2.5"
                  style={{
                    background: `linear-gradient(180deg, ${brandColor}e6 0%, ${brandColor} 50%, #0b1220 100%)`,
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span className="px-1.5 py-px rounded-full bg-white/95 text-[5px] font-extrabold tracking-wider text-slate-900">
                      SPONSORED
                    </span>
                    <span className="px-1.5 py-0.5 rounded-full bg-black/40 text-white text-[6px] font-bold">
                      Skip 5
                    </span>
                  </div>
                  <div className="flex justify-center">
                    <Icon className="w-10 h-10 text-white/80" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-white/75 text-[5px] font-bold tracking-widest uppercase mb-0.5">
                      Presented by {brand}
                    </p>
                    <p className="text-white font-bold text-[10px] leading-tight mb-1">
                      {brandTagline}
                    </p>
                    <div className="rounded-full bg-white py-1 text-center text-[6px] font-bold text-slate-900">
                      Find out more
                    </div>
                  </div>
                </div>
              )}

              {(placement === "sheet_offers" || placement === "deal_code") && (
                <div className="absolute left-0 right-0 bottom-0 bg-white rounded-t-xl p-2 shadow-[0_-8px_20px_rgba(0,0,0,0.12)]">
                  <div className="w-6 h-0.5 rounded-full bg-slate-200 mx-auto mb-1.5" />
                  {placement === "sheet_offers" ? (
                    <div className="rounded-lg p-2" style={{ background: brandColor }}>
                      <div className="flex gap-1 mb-1">
                        <span className="px-1 py-px rounded-full bg-white/95 text-[4.5px] font-extrabold tracking-wider text-slate-900">
                          SPONSORED
                        </span>
                        <span className="px-1 py-px rounded-full bg-amber-300 text-[4.5px] font-extrabold tracking-wider text-slate-900">
                          DEAL
                        </span>
                      </div>
                      <p className="text-white/80 text-[5px] font-bold uppercase tracking-wider">{brand}</p>
                      <p className="text-white font-bold text-[7px] leading-tight">{brandTagline}</p>
                      <p className="text-white text-[5.5px] font-bold mt-1">See the deals ›</p>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: brandColor }}>
                          <Icon className="w-2.5 h-2.5 text-white" />
                        </div>
                        <p className="text-slate-900 font-bold text-[7px]">{brand}</p>
                      </div>
                      <div className="rounded-lg border border-dashed border-emerald-500 bg-emerald-50 py-1.5 text-center">
                        <p className="text-emerald-700 text-[4.5px] font-extrabold tracking-wider">YOUR H-PLATE DEAL</p>
                        <p className="text-slate-900 font-black text-[11px] tracking-wider leading-tight">FRESH10</p>
                        <p className="text-slate-500 text-[4.5px]">Show this screen at the counter</p>
                      </div>
                      <div className="mt-1.5 rounded-full bg-emerald-600 py-1 text-center text-white text-[5.5px] font-bold">
                        Walk there · 5 min
                      </div>
                    </>
                  )}
                </div>
              )}

              {placement === "home_banner" && (
                <div className="absolute top-2 left-2 right-2 rounded-xl p-2 shadow-lg" style={{ background: brandColor }}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="px-1.5 py-px rounded-full bg-white/95 text-[5px] font-extrabold tracking-wider text-slate-900">
                      SPONSORED
                    </span>
                    <span className="text-white/70 text-[8px] leading-none">×</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-5 rounded-md bg-white/15 border border-white/25 flex items-center justify-center shrink-0">
                      <Icon className="w-3 h-3 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-bold text-[8px] leading-tight truncate">{brand}</p>
                      <p className="text-white/85 text-[6px] leading-tight truncate">{brandTagline}</p>
                    </div>
                  </div>
                </div>
              )}

              {placement === "festival_pin" && (
                <>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center"
                      style={{ background: brandColor, boxShadow: `0 0 0 6px ${brandColor}30, 0 0 0 12px ${brandColor}15` }}
                    >
                      <PartyPopper className="w-3 h-3 text-white" />
                    </div>
                  </div>
                  <div className="absolute top-2 left-2 right-2 rounded-md px-2 py-1 text-center" style={{ background: brandColor }}>
                    <p className="text-white text-[7px] font-bold">{brand} · On now</p>
                  </div>
                </>
              )}

              {placement === "shop_local" && (
                <div className="absolute top-1/3 left-1/2 -translate-x-1/2">
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center mx-auto"
                    style={{ background: brandColor, boxShadow: `0 2px 8px ${brandColor}80` }}
                  >
                    <Icon className="w-2.5 h-2.5 text-white" />
                  </div>
                  <div className="mt-1 px-1.5 py-0.5 rounded-md text-center" style={{ background: brandColor }}>
                    <p className="text-white text-[6px] font-bold whitespace-nowrap">{brand}</p>
                  </div>
                </div>
              )}

              {placement === "ride_complete" && (
                <div className="absolute inset-0 bg-black/45 flex items-center justify-center p-2">
                  <div className="w-full bg-white rounded-xl p-2 shadow-2xl">
                    <p className="text-[6px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                      Ride complete · EC$11
                    </p>
                    <div className="rounded-xl p-1.5" style={{ background: brandColor }}>
                      <span className="inline-block px-1.5 py-px rounded-full bg-white/95 text-[5px] font-extrabold tracking-wider text-slate-900 mb-1">
                        SPONSORED
                      </span>
                      <div className="flex items-center gap-1.5">
                        <div className="w-4 h-4 rounded-md bg-white/15 border border-white/25 flex items-center justify-center shrink-0">
                          <Icon className="w-2.5 h-2.5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-bold text-[7px] leading-tight truncate">{brand}</p>
                          <p className="text-white/85 text-[5px] leading-tight truncate">{brandTagline}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white p-1.5">
              <div className="flex items-center gap-1">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                <p className="text-[7px] text-slate-700 font-semibold flex-1 truncate">Roseau → Portsmouth</p>
                <p className="text-[7px] text-emerald-700 font-bold">3 min</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Static content ──────────────────────────────────────────────────── */

const FACTS = [
  { value: "18", label: "Bus routes in the app" },
  { value: "4", label: "Ad moments per trip" },
  { value: "Local", label: "Target riders near your door" },
  { value: "EC$", label: "Invoiced on the island" },
];

const SECTORS = ["Hotels and guest houses", "Restaurants and bars", "Shops and markets", "Banks and telecoms", "Festivals and events", "Tourism"];

interface AdPlacement {
  key: AdMockupProps["placement"];
  title: string;
  body: string;
  brand: string;
  brandTagline: string;
  brandColor: string;
  icon: React.ElementType;
}

const AD_PLACEMENTS: AdPlacement[] = [
  {
    key: "launch_splash",
    title: "Launch splash",
    body: "A full-screen photo or short video the moment someone opens H-Plate, with your name, a headline and one button. It runs for 6 seconds, riders can skip it, and each person sees it at most once a day.",
    brand: "Your Brand",
    brandTagline: "Your headline, full screen",
    brandColor: "#9a3412",
    icon: Megaphone,
  },
  {
    key: "home_banner",
    title: "Home banner",
    body: "A banner across the top of the map when a rider opens the app. Good for hotels, banks and brand awareness.",
    brand: "Your Hotel",
    brandTagline: "Weekend rates · Roseau",
    brandColor: "#1e3a5f",
    icon: Hotel,
  },
  {
    key: "sheet_offers",
    title: "Trip offer cards",
    body: "Cards inside the panel riders use to plan a trip: while they pick a destination, confirm the fare and ride. Show them only to riders near your stop.",
    brand: "Your Market",
    brandTagline: "Fresh from the farm today",
    brandColor: "#166534",
    icon: ShoppingBasket,
  },
  {
    key: "deal_code",
    title: "Deals with a code",
    body: "Add a code like FRESH10 to any card or pin. Riders show the screen at your counter, and your report tells you how many people opened the deal.",
    brand: "Your Market",
    brandTagline: "10% off fresh produce",
    brandColor: "#166534",
    icon: ShoppingBasket,
  },
  {
    key: "ride_complete",
    title: "Ride complete screen",
    body: "Shown the moment the rider arrives and checks their fare, with nothing else competing for attention. Good for restaurants and bars near drop-off points.",
    brand: "Your Restaurant",
    brandTagline: "Dinner by the Bayfront",
    brandColor: "#7f1d1d",
    icon: Utensils,
  },
  {
    key: "shop_local",
    title: "Featured map pin",
    body: "Your business as a branded pin on the live map. Riders tap it for your hours and get a Walk there or Take a bus there button.",
    brand: "Your Shop",
    brandTagline: "Roseau",
    brandColor: "#b45309",
    icon: Store,
  },
];

interface ChannelComparison {
  channel: string;
  hplate: boolean;
  print: boolean;
  social: boolean;
  billboard: boolean;
}

const COMPARISON: ChannelComparison[] = [
  { channel: "Reaches people moving around Dominica", hplate: true, print: true, social: false, billboard: true },
  { channel: "Targeted to riders near your business", hplate: true, print: false, social: true, billboard: false },
  { channel: "Scheduled by time of day and festival", hplate: true, print: false, social: true, billboard: false },
  { channel: "Report on views, taps and deal opens", hplate: true, print: false, social: true, billboard: false },
  { channel: "Pay in EC$ on the island", hplate: true, print: true, social: false, billboard: true },
];

interface PricingTier {
  name: string;
  pitch: string;
  features: string[];
  cta: string;
  highlight?: boolean;
}

const PRICING_TIERS: PricingTier[] = [
  {
    name: "Local listing",
    pitch: "For restaurants, shops, salons and small businesses.",
    features: [
      "Branded pin on the live map",
      "Your hours, plus Walk there and Take a bus there",
      "Offer cards for riders near your stop",
      "Optional deal code for your counter",
      "Monthly report",
    ],
    cta: "Start a listing",
  },
  {
    name: "Sponsor",
    pitch: "For hotels, banks, telecoms and retail chains.",
    features: [
      "Home banner",
      "Offer cards from planning to arrival",
      "Ride complete screen",
      "Featured map pin",
      "We design the ad from your photo or logo",
      "Report with views, taps and deal opens",
    ],
    cta: "Book a sponsor slot",
    highlight: true,
  },
  {
    name: "Festival takeover",
    pitch: "Your name on a festival, for the dates of the event.",
    features: [
      "Full-screen launch splash (photo or video)",
      "Home banner and trip offer cards",
      "Your name on the festival in What's on",
      "Festival map pins and ride complete screen",
      "Post-event report",
    ],
    cta: "Sponsor a festival",
  },
];

const FESTIVALS = [
  { name: "Carnival (Mas Domnik)", when: "February" },
  { name: "Jazz 'n Creole", when: "May" },
  { name: "World Creole Music Festival", when: "October" },
  { name: "Independence Day", when: "3 November" },
];

const FAQS = [
  {
    q: "How do I pay if I'm not in Dominica?",
    a: "Overseas sponsors pay by USD or EUR wire transfer to our Dominica-registered account. Local sponsors can pay by cheque or bank transfer. We invoice in EC$ and don't need a credit card.",
  },
  {
    q: "How quickly can my campaign go live?",
    a: "Local listings can go live within a day. Sponsor campaigns take 24 to 48 hours once you approve the ad. Festival slots are booked ahead, usually 2 to 4 weeks before the event.",
  },
  {
    q: "Do you design the ad for me?",
    a: "Yes. Send us a photo, a short video or just your logo and we'll build the ad. Before it goes live we show it to you running in the real app on a phone.",
  },
  {
    q: "How do I know it worked?",
    a: "Every campaign comes with a report showing how many times your ad was seen, how many people tapped it, and how many opened your deal code. We can pause your ad or swap the picture any time.",
  },
  {
    q: "Can I be the only brand in my industry?",
    a: "Yes. Telecoms, banks and tourism businesses can book category exclusivity, so no competing brand in your industry runs at the same time. Ask us for pricing.",
  },
  {
    q: "What about Government announcements?",
    a: "Public-service announcements from Government of Dominica ministries run free of charge. Get in touch to discuss what you'd like to share.",
  },
];

/* ─── Small building blocks ───────────────────────────────────────────── */

/** Small uppercase label above a section heading. */
function Eyebrow({ children, light = false }: { children: React.ReactNode; light?: boolean }) {
  return (
    <p className={`text-sm font-semibold uppercase tracking-[0.14em] ${light ? "text-emerald-300" : "text-emerald-700"}`}>
      {children}
    </p>
  );
}

/** Tick or cross for the comparison table. */
function Mark({ yes, strong = false }: { yes: boolean; strong?: boolean }) {
  return yes ? (
    <Check className={`w-4 h-4 mx-auto ${strong ? "text-emerald-600" : "text-slate-400"}`} strokeWidth={strong ? 3 : 2} aria-label="Yes" />
  ) : (
    <XIcon className="w-4 h-4 mx-auto text-slate-300" aria-label="No" />
  );
}

/* ─── Page ────────────────────────────────────────────────────────────── */

/**
 * The advertising page at h-plate.com/advertisers.
 */
export default function Advertisers() {
  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-900 overflow-x-hidden">
      <Navbar overlay />

      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-[#04241b]">
        <picture>
          <source srcSet="/images/soufriere-bay-1280.webp 1280w, /images/soufriere-bay-2400.webp 2400w" sizes="100vw" />
          <img
            src="/images/soufriere-bay-2400.webp"
            alt="Soufrière Bay on the south-west coast of Dominica"
            className="absolute inset-0 -z-10 h-full w-full object-cover object-[center_80%]"
            fetchPriority="high"
          />
        </picture>
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              "linear-gradient(90deg, rgba(3,30,22,0.92) 0%, rgba(3,30,22,0.8) 45%, rgba(3,30,22,0.35) 100%)",
          }}
          aria-hidden
        />

        <div className="container pt-36 pb-24 md:pt-44 md:pb-32">
          <div className="max-w-2xl text-white">
            <p className="text-sm font-semibold uppercase tracking-[0.14em] text-emerald-300">Advertise on H-Plate</p>
            <h1 className="mt-5 font-display text-[44px] leading-[1.05] sm:text-6xl lg:text-[68px] tracking-tight text-white">
              Reach people as they move around Dominica
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-white/80">
              H-Plate is the bus app for Dominica. From hotel guests to
              school commuters, riders open it before, during and after
              every trip. Reach them near your door or across a whole
              festival, pay in EC$, and see a clear report on what your ad did.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <a
                href={mailto("Advertising inquiry")}
                className="inline-flex items-center gap-2 h-12 px-6 rounded-lg bg-white text-emerald-900 font-semibold no-underline hover:bg-emerald-50 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Talk to our team
              </a>
              <a
                href="#placements"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-lg border border-white/40 text-white font-semibold no-underline hover:bg-white/10 transition-colors"
              >
                See ad placements
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FACTS */}
      <section className="border-b border-slate-200">
        <div className="container">
          <dl className="grid grid-cols-2 md:grid-cols-4">
            {FACTS.map((fact, i) => (
              <div
                key={fact.label}
                className={`py-8 md:py-10 px-4 text-center ${i % 2 === 1 ? "border-l border-slate-200" : ""} ${
                  i >= 2 ? "border-t md:border-t-0 border-slate-200" : ""
                } ${i === 2 ? "md:border-l" : ""}`}
              >
                <dt className="sr-only">{fact.label}</dt>
                <dd className="font-display text-4xl md:text-[44px] leading-none text-emerald-900">{fact.value}</dd>
                <dd className="mt-2 text-sm text-slate-500">{fact.label}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* SECTORS */}
      <section className="py-10">
        <div className="container">
          <p className="text-center text-sm text-slate-500">
            <span className="font-semibold text-slate-700">Built for</span>{" "}
            {SECTORS.join("  ·  ")}
          </p>
        </div>
      </section>

      {/* PLACEMENTS */}
      <section id="placements" className="scroll-mt-20 py-20 md:py-28 bg-[#f7f6f2]">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7">
              <Eyebrow>Where your ad appears</Eyebrow>
              <h2 className="mt-3 font-display text-4xl md:text-5xl tracking-tight text-slate-900">
                A few good spots, never a wall of banners
              </h2>
            </div>
            <p className="lg:col-span-5 text-lg leading-relaxed text-slate-600">
              No sound, nothing during an emergency, and every ad is
              something a rider can use on their trip. The businesses shown
              are examples.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {AD_PLACEMENTS.map((p) => (
              <article key={p.key} className="flex flex-col rounded-2xl bg-white border border-slate-200 overflow-hidden">
                <div className="bg-[#eef5f1] py-10">
                  <AdMockup
                    brand={p.brand}
                    brandTagline={p.brandTagline}
                    brandColor={p.brandColor}
                    placement={p.key}
                    icon={p.icon}
                  />
                </div>
                <div className="p-7">
                  <h3 className="font-display text-2xl tracking-tight text-slate-900">{p.title}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-slate-600">{p.body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="py-20 md:py-28">
        <div className="container max-w-5xl">
          <Eyebrow>Why H-Plate</Eyebrow>
          <h2 className="mt-3 font-display text-4xl md:text-5xl tracking-tight text-slate-900">
            How it compares
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-slate-600 max-w-2xl">
            Next to print, social media and roadside billboards.
          </p>

          <div className="mt-10 overflow-x-auto rounded-2xl border border-slate-200">
            <table className="w-full min-w-[560px] text-[15px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left px-6 py-4 text-xs font-semibold uppercase tracking-wider text-slate-500">What you get</th>
                  <th className="px-4 py-4 text-center text-sm font-semibold text-emerald-800">H-Plate</th>
                  <th className="px-4 py-4 text-center text-sm font-medium text-slate-500">Print</th>
                  <th className="px-4 py-4 text-center text-sm font-medium text-slate-500">Social</th>
                  <th className="px-4 py-4 text-center text-sm font-medium text-slate-500">Billboard</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {COMPARISON.map((row) => (
                  <tr key={row.channel}>
                    <td className="px-6 py-4 text-slate-700">{row.channel}</td>
                    <td className="px-4 py-4 bg-emerald-50/50"><Mark yes={row.hplate} strong /></td>
                    <td className="px-4 py-4"><Mark yes={row.print} /></td>
                    <td className="px-4 py-4"><Mark yes={row.social} /></td>
                    <td className="px-4 py-4"><Mark yes={row.billboard} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="bg-[#04241b] text-white py-20 md:py-28">
        <div className="container">
          <div className="max-w-2xl">
            <Eyebrow light>Pricing</Eyebrow>
            <h2 className="mt-3 font-display text-4xl md:text-5xl tracking-tight text-white">
              A plan for every size of business
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-white/70">
              No bidding and no confusing rates. Pick the package that fits
              and we'll send a rate card built around your goals, billed in
              Eastern Caribbean dollars.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {PRICING_TIERS.map((tier) => (
              <div
                key={tier.name}
                className={`flex flex-col rounded-2xl p-8 ${
                  tier.highlight ? "bg-white text-slate-900" : "border border-white/15 text-white"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <h3 className={`font-display text-2xl ${tier.highlight ? "text-slate-900" : "text-white"}`}>{tier.name}</h3>
                  {tier.highlight && (
                    <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-200">
                      Recommended
                    </span>
                  )}
                </div>
                <p className={`mt-2 text-[15px] ${tier.highlight ? "text-slate-600" : "text-white/65"}`}>{tier.pitch}</p>
                <p className={`mt-6 text-sm font-semibold ${tier.highlight ? "text-slate-900" : "text-white"}`}>Rate card on request</p>
                <ul className="mt-5 space-y-3 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className={`flex items-start gap-3 text-[15px] ${tier.highlight ? "text-slate-700" : "text-white/80"}`}>
                      <Check className={`w-4 h-4 mt-1 shrink-0 ${tier.highlight ? "text-emerald-600" : "text-emerald-300"}`} strokeWidth={2.5} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={mailto(tier.cta)}
                  className={`mt-8 inline-flex items-center justify-center gap-2 h-12 rounded-lg font-semibold no-underline transition-colors ${
                    tier.highlight
                      ? "bg-emerald-700 text-white hover:bg-emerald-800"
                      : "border border-white/30 text-white hover:bg-white/10"
                  }`}
                >
                  {tier.cta}
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            ))}
          </div>

          <p className="mt-10 text-[15px] text-white/60">
            Need something else, like a sponsored stop or industry
            exclusivity?{" "}
            <a href={mailto("Custom advertising inquiry")} className="text-white underline underline-offset-4 hover:text-emerald-200">
              Talk to our team
            </a>
            .
          </p>
        </div>
      </section>

      {/* FESTIVALS */}
      <section className="py-20 md:py-28">
        <div className="container grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <div>
            <Eyebrow>Festival sponsorships</Eyebrow>
            <h2 className="mt-3 font-display text-4xl md:text-5xl tracking-tight text-slate-900">
              Be the name on the island's biggest weeks
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              These are the weeks the whole island is on the move. A festival
              takeover puts your name on the launch splash, the top banner,
              the trip cards and the festival map pins, for the dates of the
              event. One title sponsor per festival.
            </p>
            <ul className="mt-8 divide-y divide-slate-200 border-y border-slate-200">
              {FESTIVALS.map((f) => (
                <li key={f.name} className="flex items-center justify-between gap-4 py-4">
                  <span className="flex items-center gap-3 font-medium text-slate-800">
                    <Calendar className="w-4 h-4 text-emerald-700" />
                    {f.name}
                  </span>
                  <span className="text-[15px] text-slate-500">{f.when}</span>
                </li>
              ))}
            </ul>
            <a
              href={mailto("Festival sponsorship")}
              className="mt-9 inline-flex items-center gap-2 h-12 px-6 rounded-lg bg-emerald-700 text-white font-semibold no-underline hover:bg-emerald-800 transition-colors"
            >
              Reserve a festival
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          <div className="rounded-[2rem] bg-[#eef5f1] py-14">
            <AdMockup
              brand="Your Festival"
              brandTagline="Presented by your brand"
              brandColor="#9f1239"
              placement="festival_pin"
              icon={PartyPopper}
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-[#f7f6f2]">
        <div className="container max-w-5xl">
          <Eyebrow>Questions</Eyebrow>
          <h2 className="mt-3 font-display text-4xl md:text-5xl tracking-tight text-slate-900">
            Common questions
          </h2>
          <dl className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-12">
            {FAQS.map((faq) => (
              <div key={faq.q} className="border-t border-slate-300/70 py-7">
                <dt className="font-sans text-lg font-semibold text-slate-900">{faq.q}</dt>
                <dd className="mt-2 text-[15px] leading-relaxed text-slate-600">{faq.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 md:py-28">
        <div className="container">
          <div className="rounded-[2rem] bg-[#04241b] px-6 py-14 md:px-16 md:py-20 text-center">
            <h2 className="font-display text-4xl md:text-5xl tracking-tight text-white">
              Let's plan your campaign
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-white/70 max-w-xl mx-auto">
              Email our partnerships team with what you'd like to promote and
              we'll come back with a proposal.
            </p>
            <a
              href={mailto("Advertising inquiry")}
              className="mt-9 inline-flex items-center gap-2 h-12 px-6 rounded-lg bg-white text-emerald-900 font-semibold no-underline hover:bg-emerald-50 transition-colors"
            >
              <Mail className="w-4 h-4" />
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
